import { enableScrolling, disableScrolling } from './utils';

let currentModal: HTMLElement | null = null;
let currentTrigger: HTMLElement | null = null;

/**
 * Get all tabbable elements on the page
 */
function getPageButtons() {
  return document.querySelectorAll(`
    select:not([tabindex="-1"]),
    input:not([tabindex="-1"]),
    textarea:not([tabindex="-1"]),
    button:not([tabindex="-1"]),
    a:not([tabindex="-1"]),
    iframe:not([tabindex="-1"])
  `);
}

/**
 * Check if modal is allowed to exit
 */
const isExitAllowed = (modal: HTMLElement) => {
  if (!modal.hasAttribute('data-locked')) {
    return true;
  }
  if (['false', '0'].includes(modal.dataset.locked || '')) {
    return true;
  }
  return false;
};

/**
 * Open modal
 */
export function openModal(modalName: string, trigger?: HTMLElement) {
  const modal = document.querySelector<HTMLElement>(`.js-modal[data-modal="${modalName}"]`);
  if (!modal) {
    return;
  }

  currentModal = modal;
  currentTrigger = trigger || null;

  modal.removeAttribute('inert');

  const content = modal.querySelector<HTMLElement>('.js-modal-content');
  if (content) {
    disableScrolling();
  }

  modal.classList.add('modal--is-visible');

  // Disable tabbing outside modal
  const pageBtns = getPageButtons();
  pageBtns.forEach((btn) => {
    btn.setAttribute('tabindex', '-1');
    (btn as HTMLElement).dataset.tabindex = 'modified';
  });

  // Enable tabbing inside modal
  const modalLinks = modal.querySelectorAll<HTMLElement>('select, input, textarea, button, a, iframe');
  modalLinks.forEach((link) => link.setAttribute('tabindex', '0'));
  if (modalLinks.length) {
    modalLinks[0].focus();
  }
}

/**
 * Close modal
 */
export function closeModal(modalName?: string) {
  const modal = modalName
    ? document.querySelector<HTMLElement>(`.js-modal[data-modal="${modalName}"]`)
    : currentModal;

  if (!modal) {
    return;
  }

  // Restore focus
  if (currentTrigger && document.body.contains(currentTrigger)) {
    currentTrigger.focus();
  } else {
    document.body.focus();
  }

  enableScrolling();

  // Hide modal & set inert
  modal.classList.remove('modal--is-visible');
  modal.setAttribute('inert', '');

  // Remove tabbing inside modal
  const modalLinks = modal.querySelectorAll('select, input, textarea, button, a, iframe');
  modalLinks.forEach((link) => link.setAttribute('tabindex', '-1'));

  // Restore tabbing outside modal
  const pageBtns = document.querySelectorAll(`
    select[data-tabindex="modified"],
    input[data-tabindex="modified"],
    textarea[data-tabindex="modified"],
    button[data-tabindex="modified"],
    a[data-tabindex="modified"],
    iframe[data-tabindex="modified"]
  `);
  pageBtns.forEach((btn) => {
    btn.setAttribute('tabindex', '0');
    btn.removeAttribute('data-tabindex');
  });

  modal.dispatchEvent(new CustomEvent('close', { bubbles: true, detail: { modal: modalName } }));

  currentModal = null;
  currentTrigger = null;
}

/**
 * Handle multi-page modals
 */
function initModalPages(modal: HTMLElement) {
  const pages = modal.querySelectorAll<HTMLElement>('[data-page]');

  if (!pages.length) {
    return;
  }

  function showPage(pageId: string) {
    pages.forEach((page) => {
      const isActive = page.dataset.page === pageId;

      const focusables = page.querySelectorAll<HTMLElement>(
        'a, button, input, select, textarea, [tabindex]'
      );

      page.setAttribute('aria-hidden', String(!isActive));

      focusables.forEach((el) => {
        if (isActive) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', '-1');
        }
      });

      if (isActive && focusables.length) {
        // Delay ensures modal visibility + inert removal has settled
        requestAnimationFrame(() => focusables[0].focus());
      }
    });
  }

  // Next / jump page buttons
  modal.querySelectorAll<HTMLElement>('.js-modal-next-page').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const target = btn.dataset.target;
      if (target) {
        showPage(target);
      }
    });
  });

  // Default page
  showPage('1');

  // Reset pages when modal closes
  modal.addEventListener('close', () => {
    // Match your old delay to avoid visual/focus glitches
    setTimeout(() => {
      showPage('1');
    }, 300);
  });
}

/**
 * Wire up all modals on the page. Call once on page load.
 */
export function initModals() {
  // Multi-page modal setup
  document.querySelectorAll<HTMLElement>('.js-modal').forEach((modal) => {
    initModalPages(modal);
  });

  // Open modal when clicking a trigger button
  document.querySelectorAll<HTMLElement>('.js-modal-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openModal(btn.getAttribute('data-modal')!, btn);
    });
  });

  // Close modal when clicking a close button
  document.querySelectorAll<HTMLElement>('.js-modal-close').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const modal = btn.closest<HTMLElement>('.js-modal');
      if (modal) {
        closeModal(modal.dataset.modal);
      }
    });
  });

  // Close modal when clicking on overlay (outside modal content)
  document.querySelectorAll<HTMLElement>('.js-modal').forEach((modal) => {
    modal.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.js-modal-content')) {
        if (isExitAllowed(modal)) {
          closeModal(modal.dataset.modal);
        } else {
          console.debug('Modal is locked, cannot close');
        }
      }
    });
  });

  // Close modal when pressing ESC
  document.addEventListener('keyup', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      // Pick the topmost visible modal if multiple are open
      const visibleModals = document.querySelectorAll<HTMLElement>('.modal--is-visible');
      if (visibleModals.length === 0) {
        return;
      }

      const modal = visibleModals[visibleModals.length - 1];
      if (isExitAllowed(modal)) {
        closeModal(modal.dataset.modal);
      } else {
        console.debug('Modal is locked, cannot close');
      }
    }
  });
}
